import { computed, ref, watch, type Ref } from "vue";
import Unipile from "@/services/unipile/unipile";
import type { Message, Conversation } from "@/dtos/Unipile.dto";
import type { WhatsAppMessagesParams } from "./types";

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

function getSenderName(senderId: string | undefined, conversation: Conversation | null): string {
  if (!senderId) return "Unknown";
  if (!conversation?.participants || !Array.isArray(conversation.participants)) {
    return "Unknown";
  }

  const participant = conversation.participants.find(
    (p) => p.providerUserId === senderId || (p as any).sender_id === senderId || p.id === senderId
  );

  if (participant) {
    return (
      participant.name ||
      (participant as any).displayName ||
      (participant as any).attendee_name ||
      (participant as any).attendeeName ||
      "Unknown"
    );
  }

  return "Unknown";
}

function transformMessage(msg: any, conversation: Conversation | null): Message {
  let senderName = "Unknown";

  if (msg.is_sender || msg.sender_id === "me" || msg.isOwn) {
    senderName = "You";
  } else if (msg.senderName) {
    senderName = msg.senderName;
  } else if (msg.sender_id) {
    senderName = getSenderName(msg.sender_id, conversation);
  }

  const quoted = msg.reply_to
    ? {
        id: msg.reply_to.id,
        text: msg.reply_to.text,
        senderName:
          msg.reply_to.sender_id === msg.sender_id
            ? "You"
            : getSenderName(msg.reply_to.sender_id, conversation),
        sender_id: msg.reply_to.sender_id,
        attachments: msg.reply_to.attachments || [],
      }
    : msg.quoted || null;

  let readReceipts = msg.readReceipts || [];

  if ((!readReceipts || readReceipts.length === 0) && msg.seen_by) {
    readReceipts = Object.entries(msg.seen_by).map(([id, timestamp]) => ({
      id,
      readAt: timestamp as string,
      readBy: id,
    }));
  }

  return {
    ...msg,
    senderName,
    quoted,
    readReceipts,
    readCount: readReceipts.length,
  };
}

export function useWhatsAppMessages(
  params: Ref<WhatsAppMessagesParams | null> | WhatsAppMessagesParams | null,
  conversation: Ref<Conversation | null> | Conversation | null = null
) {
  const messages = ref<Message[]>([]);
  const isLoadingMessages = ref(false);
  const isLoadingOlderMessages = ref(false);
  const hasMoreMessages = ref(true);
  const oldestMessageTimestamp = ref<string | null>(null);

  const currentParams = computed(() => {
    return isRef(params) ? params.value : params;
  });

  const currentConversation = computed(() => {
    return isRef(conversation) ? conversation.value : conversation;
  });

  const loadMessages = async (conversationId: string, loadOlder = false) => {
    if (!conversationId) {
      messages.value = [];
      hasMoreMessages.value = true;
      oldestMessageTimestamp.value = null;
      return;
    }

    try {
      if (loadOlder) {
        isLoadingOlderMessages.value = true;
      } else {
        isLoadingMessages.value = true;
        messages.value = [];
        hasMoreMessages.value = true;
        oldestMessageTimestamp.value = null;
      }

      const requestParams: any = {
        limit: currentParams.value?.limit || 10,
      };

      if (loadOlder && oldestMessageTimestamp.value) {
        requestParams.before = oldestMessageTimestamp.value;
      }

      const response = await Unipile.getConversationMessages(conversationId, requestParams);

      if (!response || !response.data) {
        if (!loadOlder) {
          messages.value = [];
        }
        return;
      }

      const responseData: any = response.data.data || response.data;
      let rawMessages: any[] = [];

      if (responseData && responseData.items && Array.isArray(responseData.items)) {
        rawMessages = responseData.items;
      } else if (Array.isArray(responseData)) {
        rawMessages = responseData;
      } else {
        rawMessages = [];
      }

      const conv = currentConversation.value;

      const processedMessages: Message[] = rawMessages.map((msg: any) =>
        transformMessage(msg, conv)
      );

      hasMoreMessages.value = responseData.has_more !== false;
      if (responseData.oldest_timestamp) {
        oldestMessageTimestamp.value = responseData.oldest_timestamp;
      }

      if (loadOlder) {
        messages.value = [...processedMessages, ...messages.value];
      } else {
        messages.value = processedMessages;
      }
    } catch (error) {
      console.error("Failed to load WhatsApp messages:", error);
      if (!loadOlder) {
        messages.value = [];
      }
    } finally {
      if (loadOlder) {
        isLoadingOlderMessages.value = false;
      } else {
        isLoadingMessages.value = false;
      }
    }
  };

  const handleLoadOlderMessages = () => {
    const p = currentParams.value;
    if (p?.conversationId && hasMoreMessages.value && !isLoadingOlderMessages.value) {
      loadMessages(p.conversationId, true);
    }
  };

  watch(
    () => currentParams.value?.conversationId,
    (conversationId) => {
      if (conversationId) {
        loadMessages(conversationId, false);
      } else {
        messages.value = [];
        hasMoreMessages.value = true;
        oldestMessageTimestamp.value = null;
      }
    },
    { immediate: true }
  );

  return {
    messages,
    isLoadingMessages,
    isLoadingOlderMessages,
    hasMoreMessages,
    oldestMessageTimestamp,
    loadMessages,
    handleLoadOlderMessages,
  };
}
